import { resolveArtifactDownloadUrl } from './artifact-download';
import type { Source } from '../types';

const PRESIGN_ROUTE = '/presign';
const DOWNLOAD_ROUTE = '/download';

/** Turn an artifact's presign path into a URL that streams the file's bytes.
 *
 *  The agent emits `/api/v1/artifacts/{id}/presign`, which returns a tiny JSON
 *  body `{ url, filename }` — correct for a download (hand the S3 link to the
 *  browser) but useless for a preview, which needs the bytes themselves. The
 *  sibling `/download` route 302s straight to the same presigned S3 URL, so it
 *  behaves exactly like the KB view endpoint `useAuthenticatedBlob` already
 *  handles: the session bearer rides the same-origin call, and the browser
 *  strips it when the redirect crosses to S3 (which would otherwise reject the
 *  request — an Authorization header isn't part of a query-string signature).
 *
 *  Also applies the `/api/v1/` → `/api/chat-agent/v1/` proxy rewrite; without
 *  it the relative fetch resolves against the SPA origin and returns the
 *  index.html shell instead of the file. */
export function artifactPreviewUrl(downloadUrl: string): string {
  if (!downloadUrl) return '';
  const streamed = downloadUrl.endsWith(PRESIGN_ROUTE)
    ? `${downloadUrl.slice(0, -PRESIGN_ROUTE.length)}${DOWNLOAD_ROUTE}`
    : downloadUrl;
  return resolveArtifactDownloadUrl(streamed);
}

/** Inverse of `artifactPreviewUrl`: recover the presign route from the preview
 *  url, which is all a preview panel holds.
 *
 *  A download from the preview needs it. The panel renders the fetched bytes
 *  from a `blob:` URL, and a browser names a file saved from one after the
 *  blob's UUID. The presigned S3 link carries `Content-Disposition` with the
 *  artifact's filename instead, the same path the artifact card's own
 *  download takes.
 *
 *  Null when the url is not a `/download` route (an absolute link that
 *  `artifactPreviewUrl` passed through untouched): there is no presign route
 *  behind it. */
export function artifactPresignUrl(previewUrl: string): string | null {
  if (!previewUrl.endsWith(DOWNLOAD_ROUTE)) return null;
  return `${previewUrl.slice(0, -DOWNLOAD_ROUTE.length)}${PRESIGN_ROUTE}`;
}

/** Adapt a generated artifact into the `Source` shape the existing preview
 *  panels already consume, so neither panel needs to learn about artifacts.
 *
 *  `type: 'artifact'` is what routes it to the extension-based branch in
 *  `resolveFilePreviewKind`; `citationIndex: 0` marks it as uncited (an
 *  artifact is produced by the conversation, not referenced by it). */
export interface ArtifactRef {
  readonly artifactId?: string;
  readonly filename?: string;
  readonly downloadUrl?: string;
}

export function artifactAsSource(artifact: ArtifactRef): Source {
  const filename = artifact.filename || 'file';
  return {
    id: artifact.artifactId || filename,
    citationIndex: 0,
    type: 'artifact',
    title: filename,
    description: '',
    url: artifactPreviewUrl(artifact.downloadUrl || ''),
    metadata: {} as Source['metadata'],
  };
}

/** Pick the artifact a PreviewArtifact action refers to.
 *
 *  Prefers the filename/downloadUrl bound directly on the event, and falls back
 *  to looking the file up in the group's `files` list by `artifactId`. The
 *  fallback matters because the two are bound by separate code in another repo:
 *  if an emission ever carries only the list plus an id, the eye button should
 *  still open rather than silently doing nothing. */
export function resolveArtifactRef(payload: {
  readonly filename?: unknown;
  readonly downloadUrl?: unknown;
  readonly artifactId?: unknown;
  readonly files?: unknown;
}): ArtifactRef | null {
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v ? v : undefined;

  const artifactId = str(payload.artifactId);
  const direct: ArtifactRef = {
    artifactId,
    filename: str(payload.filename),
    downloadUrl: str(payload.downloadUrl),
  };
  if (direct.downloadUrl) return direct;

  // Without an id there is nothing to match on, and matching anyway would
  // compare undefined to undefined and "find" the first entry that also lacks
  // an id — opening a preview of the wrong file. Prefer opening nothing.
  if (!artifactId) return null;
  if (!Array.isArray(payload.files)) return null;
  const match = payload.files.find(
    (f): f is Record<string, unknown> =>
      !!f &&
      typeof f === 'object' &&
      str((f as ArtifactRef).artifactId) === artifactId
  );
  if (!match) return null;
  const downloadUrl = str(match['downloadUrl']);
  if (!downloadUrl) return null;
  return {
    artifactId,
    filename: str(match['filename']) ?? direct.filename,
    downloadUrl,
  };
}
